/* Stacked carousel — Figma 34:2267 ("Love that got captured").
   Deliberately NOT the shared scroller: this is a z-stacked deck where the top
   card sits square and the ones behind fan out rotated. A scroll container
   cannot express that, so it gets its own small module.

   Markup contract:
     [data-aes-stack]        wrapper
     [data-aes-stack-card]   the cards, in source order
     [data-aes-stack-prev/next], [data-aes-stack-dots]
   Position is expressed as data-pos = (i - active + n) % n, and the CSS styles
   pos 0/1/2; anything further back is parked and hidden. */
(() => {
  const build = (root) => {
    if (root.dataset.aesBound) return;
    root.dataset.aesBound = '1';

    const cards = [...root.querySelectorAll('[data-aes-stack-card]')];
    const n = cards.length;
    if (!n) return;
    const prev = root.querySelector('[data-aes-stack-prev]');
    const next = root.querySelector('[data-aes-stack-next]');
    const rail = root.querySelector('[data-aes-stack-dots]');
    const MAX_FAN = 5;   // cards visible in the fan at once
    let active = 0;

    const dots = rail ? cards.map((_, i) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'aes-dot';
      b.setAttribute('aria-label', `Show item ${i + 1} of ${n}`);
      b.addEventListener('click', () => go(i));
      rail.appendChild(b);
      return b;
    }) : [];

    const paint = () => {
      cards.forEach((c, i) => {
        // distance from the front card; CSS turns this into pos * step degrees
        const pos = (i - active + n) % n;
        // only VISIBLE cards fan; anything past this parks behind the deepest
        // one and fades out, so the stack never grows past MAX_FAN
        const hidden = pos >= MAX_FAN;
        c.style.setProperty('--pos', hidden ? MAX_FAN - 1 : pos);
        c.style.zIndex = String(n - pos);
        c.style.opacity = hidden ? '0' : '1';
        c.dataset.pos = String(pos);
        c.setAttribute('aria-hidden', String(pos !== 0));
        // only the front card is reachable; the rest are decoration
        c.querySelectorAll('a, button').forEach((el) => el.tabIndex = pos === 0 ? 0 : -1);
        // video cards: the front one plays (muted, looping), the rest sit on
        // their poster — one decode at a time, and no sound ever
        const v = c.querySelector('[data-aes-stack-video]');
        if (v) {
          const still = matchMedia('(prefers-reduced-motion: reduce)').matches;
          // the front card plays only while the deck is on screen: nothing
          // downloads for a deck the shopper has not scrolled to
          if (pos === 0 && !still && root.dataset.aesInView === '1') { v.preload = 'auto'; const pr = v.play(); if (pr && pr.catch) pr.catch(() => {}); }
          else { v.pause(); }
        }
      });
      dots.forEach((d, i) => {
        const dist = Math.abs(i - active);
        d.setAttribute('aria-current', String(i === active));
        d.dataset.dist = dist >= 2 ? '2' : String(dist);
      });
      if (n < 2) { prev?.setAttribute('hidden', ''); next?.setAttribute('hidden', ''); rail?.setAttribute('hidden', ''); }
    };

    const go = (i) => { active = (i + n) % n; paint(); };
    if ('IntersectionObserver' in window) {
      new IntersectionObserver((es) => {
        root.dataset.aesInView = es.some((e) => e.isIntersecting) ? '1' : '0';
        paint();
      }, { rootMargin: '100px 0px' }).observe(root);
    } else root.dataset.aesInView = '1';
    prev?.addEventListener('click', () => go(active - 1));
    next?.addEventListener('click', () => go(active + 1));
    root.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') go(active - 1);
      if (e.key === 'ArrowRight') go(active + 1);
    });
    paint();
    // reveal only once positions are assigned, so nothing animates on first paint
    requestAnimationFrame(() => root.setAttribute('data-ready', ''));
  };

  const boot = (scope) => (scope || document).querySelectorAll('[data-aes-stack]').forEach(build);
  boot();
  document.addEventListener('shopify:section:load', (e) => boot(e.target));
})();
